import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Bell, BookOpen, Globe, Palette, ShieldCheck, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { HorizontalEdgeScrollButtons } from '../shared/HorizontalEdgeScrollButtons';
import { TOUR_TARGET_FOCUS_EVENT, type TourTargetEventDetail } from '../../learnerTour/tourTargetEvents';
import type { LearnerSettingsSectionThemeId } from './learnerSettingsSectionThemes';

export type LearnerSettingsModalTabId = 'region' | 'security' | 'notifications' | 'lessonText' | 'template';

export const LEARNER_SETTINGS_MODAL_TAB_ORDER: LearnerSettingsModalTabId[] = [
  'security',
  'notifications',
  'lessonText',
  'region',
  'template'
];

type SettingsTabThemeId = LearnerSettingsSectionThemeId | 'plum';

const TAB_META: Record<
  LearnerSettingsModalTabId,
  { labelKey: string; shortLabelKey: string; Icon: LucideIcon; themeId: SettingsTabThemeId }
> = {
  region: { labelKey: 'tabRegion', shortLabelKey: 'tabRegion', Icon: Globe, themeId: 'sky' },
  security: { labelKey: 'tabSecurity', shortLabelKey: 'tabSecurity', Icon: ShieldCheck, themeId: 'emerald' },
  notifications: { labelKey: 'notifications', shortLabelKey: 'tabAlertsShort', Icon: Bell, themeId: 'amber' },
  lessonText: { labelKey: 'tabLessonText', shortLabelKey: 'tabTextShort', Icon: BookOpen, themeId: 'violet' },
  template: { labelKey: 'tabPreferences', shortLabelKey: 'tabPreferencesShort', Icon: Palette, themeId: 'plum' }
};

/** Active label and icon color. Layout matches profile tabs: icon above the label, thin underline. */
const TAB_ACTIVE_TEXT: Record<SettingsTabThemeId, string> = {
  sky: 'text-sky-600 dark:text-sky-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  amber: 'text-amber-600 dark:text-amber-400',
  violet: 'text-violet-600 dark:text-violet-400',
  plum: 'text-[#3f2a56] dark:text-[#c4a574]'
};

export function learnerSettingsTabIcon(tabId: LearnerSettingsModalTabId): LucideIcon {
  return TAB_META[tabId].Icon;
}

export function learnerSettingsTabCarouselIndex(tabId: LearnerSettingsModalTabId, isRtl: boolean): number {
  const idx = LEARNER_SETTINGS_MODAL_TAB_ORDER.indexOf(tabId);
  return isRtl ? LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1 - idx : idx;
}

export function learnerSettingsTabCarouselOrder(isRtl: boolean): LearnerSettingsModalTabId[] {
  return isRtl ? [...LEARNER_SETTINGS_MODAL_TAB_ORDER].reverse() : LEARNER_SETTINGS_MODAL_TAB_ORDER;
}

/** Maps a quick-settings tour target (tab, list row, or inner section) to the section that should be shown. */
export function learnerSettingsTabForTourTarget(targetKey: string): LearnerSettingsModalTabId | undefined {
  if (targetKey.startsWith('quick-settings-security')) return 'security';
  if (targetKey.startsWith('quick-settings-notifications')) return 'notifications';
  if (targetKey.startsWith('quick-settings-lesson-text')) return 'lessonText';
  if (targetKey.startsWith('quick-settings-region')) return 'region';
  if (targetKey.startsWith('quick-settings-template')) return 'template';
  return undefined;
}

export interface LearnerSettingsModalTabsProps {
  active: LearnerSettingsModalTabId;
  onChange: (tab: LearnerSettingsModalTabId) => void;
  dirtyTabs?: Partial<Record<LearnerSettingsModalTabId, boolean>>;
  className?: string;
  dragOffsetPx?: number;
  isDragging?: boolean;
  carouselRef?: React.RefObject<HTMLDivElement | null>;
}

export const LearnerSettingsModalTabs: React.FC<LearnerSettingsModalTabsProps> = ({
  active,
  onChange,
  dirtyTabs,
  className = '',
  dragOffsetPx = 0,
  isDragging = false,
  carouselRef
}) => {
  const { t } = useTranslation('settings');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const labelId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabNavRowRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<LearnerSettingsModalTabId, HTMLButtonElement>>>({});
  const activeTabRef = useRef(active);
  const tourScrollInstantRef = useRef(false);
  const dragUiRef = useRef({ isDragging, dragOffsetPx });
  const isRtlRef = useRef(isRtl);
  const [indicatorStyle, setIndicatorStyle] = React.useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [tabOverflow, setTabOverflow] = useState({ moreLeft: false, moreRight: false });

  const tabVisualOrder = useCallback(
    (tabId: LearnerSettingsModalTabId) => {
      const idx = LEARNER_SETTINGS_MODAL_TAB_ORDER.indexOf(tabId);
      return isRtl ? LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1 - idx : idx;
    },
    [isRtl]
  );

  useEffect(() => {
    isRtlRef.current = isRtl;
  }, [isRtl]);

  useEffect(() => {
    const onFocus = (event: Event) => {
      const key = (event as CustomEvent<TourTargetEventDetail>).detail?.targetKey ?? '';
      if (!learnerSettingsTabForTourTarget(key)) return;
      tourScrollInstantRef.current = true;
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
  }, []);

  useEffect(() => {
    activeTabRef.current = active;
  }, [active]);

  useEffect(() => {
    dragUiRef.current = { isDragging, dragOffsetPx };
  }, [isDragging, dragOffsetPx]);

  const updateIndicator = useCallback(() => {
    const row = tabNavRowRef.current;
    if (!row) return;
    const tab = activeTabRef.current;
    const activeButton = tabRefs.current[tab];
    if (!activeButton) return;

    const { isDragging: dragging, dragOffsetPx: drag } = dragUiRef.current;
    const cards = carouselRef?.current;
    const cardWidth = cards?.clientWidth ?? 0;
    const idx = LEARNER_SETTINGS_MODAL_TAB_ORDER.indexOf(tab);
    const measureInRow = (btn: HTMLElement) => {
      const rowRect = row.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      return { left: btnRect.left - rowRect.left, width: btnRect.width };
    };

    let { left, width } = measureInRow(activeButton);

    if (dragging && cardWidth > 0) {
      const progress = isRtlRef.current ? drag / cardWidth : -drag / cardWidth;
      let neighborIdx = idx;
      if (progress > 0 && idx < LEARNER_SETTINGS_MODAL_TAB_ORDER.length - 1) neighborIdx = idx + 1;
      else if (progress < 0 && idx > 0) neighborIdx = idx - 1;

      const neighborTab = LEARNER_SETTINGS_MODAL_TAB_ORDER[neighborIdx];
      const neighborButton = neighborTab ? tabRefs.current[neighborTab] : undefined;
      if (neighborButton && neighborIdx !== idx) {
        const t = Math.min(1, Math.abs(progress));
        const neighbor = measureInRow(neighborButton);
        left = left + (neighbor.left - left) * t;
        width = width + (neighbor.width - width) * t;
      }
    }

    setIndicatorStyle((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef]);

  const updateTabOverflow = useCallback(() => {
    const nav = scrollRef.current;
    if (!nav) return;
    const canScroll = nav.scrollWidth > nav.clientWidth + 1;
    if (!canScroll) {
      setTabOverflow((prev) => (prev.moreLeft || prev.moreRight ? { moreLeft: false, moreRight: false } : prev));
      return;
    }
    const maxScroll = Math.max(0, nav.scrollWidth - nav.clientWidth);
    const moreLeft = nav.scrollLeft > 1;
    const moreRight = nav.scrollLeft < maxScroll - 1;
    setTabOverflow((prev) => (prev.moreLeft === moreLeft && prev.moreRight === moreRight ? prev : { moreLeft, moreRight }));
  }, []);

  const scrollTabNavBy = useCallback(
    (delta: number) => {
      const nav = scrollRef.current;
      if (!nav) return;
      const bump = () => updateTabOverflow();
      nav.scrollBy({ left: delta, behavior: 'smooth' });
      bump();
      requestAnimationFrame(bump);
      requestAnimationFrame(() => requestAnimationFrame(bump));
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        bump();
      };
      const timer = window.setTimeout(finish, 420);
      nav.addEventListener(
        'scrollend',
        () => {
          window.clearTimeout(timer);
          finish();
        },
        { once: true }
      );
    },
    [updateTabOverflow]
  );

  useLayoutEffect(() => {
    updateIndicator();
    updateTabOverflow();
    const btn = tabRefs.current[active];
    const nav = scrollRef.current;
    const needsTabNavScroll = nav != null && nav.scrollWidth > nav.clientWidth + 1;
    if (btn && nav && needsTabNavScroll) {
      const instant = tourScrollInstantRef.current;
      tourScrollInstantRef.current = false;
      const navRect = nav.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const delta = btnRect.left + btnRect.width / 2 - (navRect.left + navRect.width / 2);
      if (Math.abs(delta) > 1) {
        if (instant) nav.scrollLeft += delta;
        else nav.scrollBy({ left: delta, behavior: 'smooth' });
      }
      queueMicrotask(() => {
        updateIndicator();
        updateTabOverflow();
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateIndicator();
          updateTabOverflow();
        });
      });
    }
    const onResize = () => {
      updateIndicator();
      updateTabOverflow();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active, updateIndicator, updateTabOverflow, isRtl]);

  useEffect(() => {
    const nav = scrollRef.current;
    const row = tabNavRowRef.current;
    if (!nav) return;
    const onScroll = () => {
      updateIndicator();
      updateTabOverflow();
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    const observers: ResizeObserver[] = [];
    if (typeof ResizeObserver !== 'undefined') {
      if (row) {
        const rowObs = new ResizeObserver(() => {
          updateIndicator();
          updateTabOverflow();
        });
        rowObs.observe(row);
        observers.push(rowObs);
      }
      LEARNER_SETTINGS_MODAL_TAB_ORDER.forEach((tabId) => {
        const btn = tabRefs.current[tabId];
        if (!btn) return;
        const btnObs = new ResizeObserver(() => updateIndicator());
        btnObs.observe(btn);
        observers.push(btnObs);
      });
    }
    return () => {
      nav.removeEventListener('scroll', onScroll);
      observers.forEach((o) => o.disconnect());
    };
  }, [updateIndicator, updateTabOverflow, active, isRtl, t]);

  useLayoutEffect(() => {
    if (!isDragging && dragOffsetPx === 0) return;
    updateIndicator();
  }, [isDragging, dragOffsetPx, updateIndicator]);

  return (
    <nav
      aria-labelledby={labelId}
      dir="ltr"
      data-no-tab-swipe
      className={`relative shrink-0 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 ${className}`}
    >
      <p id={labelId} className="sr-only">
        {t('quickSettingsSections')}
      </p>
      <div className="relative">
      <div
        ref={scrollRef}
        onScroll={updateTabOverflow}
        className="flex w-full touch-pan-x overflow-x-auto scrollbar-hide pt-2 pb-1.5 sm:pb-2 sm:pt-4"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div ref={tabNavRowRef} role="tablist" className="relative flex min-w-max flex-nowrap gap-1 sm:mx-auto sm:gap-4">
          {LEARNER_SETTINGS_MODAL_TAB_ORDER.map((tabId) => {
            const meta = TAB_META[tabId];
            const isActive = active === tabId;
            const isDirty = dirtyTabs?.[tabId];
            const Icon = meta.Icon;
            const tone = isActive
              ? TAB_ACTIVE_TEXT[meta.themeId]
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300';
            return (
              <button
                key={tabId}
                ref={(el) => {
                  if (el) tabRefs.current[tabId] = el;
                }}
                type="button"
                role="tab"
                aria-selected={isActive ? true : false}
                tabIndex={isActive ? 0 : -1}
                data-tour-id={`quick-settings-${tabId === 'lessonText' ? 'lesson-text' : tabId}`}
                style={{ order: tabVisualOrder(tabId) }}
                onClick={() => onChange(tabId)}
                className={`touch-target flex min-w-[72px] touch-manipulation flex-col items-center justify-center whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors sm:min-w-[100px] sm:px-6 sm:py-3 sm:text-sm ${tone}`}
              >
                <span className="relative flex-shrink-0">
                  <Icon className={`mb-1 h-5 w-5 sm:mb-2 sm:h-8 sm:w-8 ${isActive ? TAB_ACTIVE_TEXT[meta.themeId] : 'text-gray-500 dark:text-gray-400'}`} aria-hidden />
                  {isDirty && (
                    <span
                      className="absolute -end-1 -top-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-gray-900"
                      aria-label={t('unsavedTabIndicator')}
                    />
                  )}
                </span>
                <span className="truncate text-center">
                  {tabId === 'template'
                    ? t(meta.labelKey, { defaultValue: isRtl ? 'ترجیحات' : 'Preferences' })
                    : t(meta.labelKey)}
                </span>
              </button>
            );
          })}
          <div
            className="pointer-events-none absolute bottom-0 h-0.5 bg-blue-500 dark:bg-blue-400"
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
              transition: isDragging ? 'none' : 'left 0.28s cubic-bezier(0.22, 1, 0.36, 1), width 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
              zIndex: 10
            }}
          />
        </div>
      </div>
      <HorizontalEdgeScrollButtons
        showStart={isRtl ? tabOverflow.moreRight : tabOverflow.moreLeft}
        showEnd={isRtl ? tabOverflow.moreLeft : tabOverflow.moreRight}
        onTowardStart={() => {
          const nav = scrollRef.current;
          const delta = nav ? Math.min(200, nav.clientWidth * 0.7) : 100;
          scrollTabNavBy(isRtl ? delta : -delta);
        }}
        onTowardEnd={() => {
          const nav = scrollRef.current;
          const delta = nav ? Math.min(200, nav.clientWidth * 0.7) : 100;
          scrollTabNavBy(isRtl ? -delta : delta);
        }}
        isRtl={isRtl}
        startAriaLabel={t('scrollTabsBack', { defaultValue: 'Scroll tabs back' })}
        endAriaLabel={t('scrollTabsForward', { defaultValue: 'Scroll tabs forward' })}
      />
      </div>
    </nav>
  );
};
